import React, { useState } from "react";

import { DateTime } from "luxon";
import * as Papa from "papaparse";

import { createColumnHelper } from "@tanstack/react-table";

import Modal from "../Shared/Modal";
import { Table } from "../Shared/Table/Table";
import useApiHelper from "../../services/useApiHelper";
import useUserContext from "../../contexts/UserContext";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import { nurtureCampaignQueryKeys } from "../../data/nurtureCampaigns/queryKeys";
import { INurtureCampaignRecipientStatistic } from "../../models/nurtureCampaign/NurtureCampaignStatistics";
import useQSBuilder from "../../hooks/useQSBuilder";
import formatDateTime from "../../utils/formatDateTime";
import LoadingWheel from "../Shared/LoadingWheel";
import { DocumentIcon } from "@heroicons/react/outline";

interface CampaignRecipientsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  nurtureRuleId: string;
}

const colHelper = createColumnHelper<INurtureCampaignRecipientStatistic>();

export function CampaignRecipientsModal({ open, setOpen, nurtureRuleId }: CampaignRecipientsModalProps) {
  const { get } = useApiHelper();
  const { user: { agency } } = useUserContext();
  const [exportLoading, setExportLoading] = useState(false);
  const buildQs = useQSBuilder();

  const handleExportCSV = async () => {
    setExportLoading(true);

    try {
      const qs = buildQs({ itemsPerPage: 1000000 })
      const res = await get(`/marketing/${agency.id}/nurture/${nurtureRuleId}/recipients${qs}`);

      parseAndDownloadCSV(res.data);

    } catch (error) {
      console.error("Error exporting CSV:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const parseAndDownloadCSV = (data: INurtureCampaignRecipientStatistic[]) => {
    const csv = Papa.unparse(data);

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `newsletter_recipients-${formatDateTime(new Date().toISOString(), DateTime.DATE_SHORT)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  const cols = [
    colHelper.accessor("email", {
      header: "Email",
    }),
    colHelper.accessor("firstName", {
      header: "First Name",
    }),
    colHelper.accessor("lastname", {
      header: "Last Name",
    }),
    colHelper.accessor("openCount", {
      header: "Opens",
    }),
    colHelper.accessor("clickCount", {
      header: "Clicks",
    }),
  ];

  return (
    <Modal
      // @ts-ignore
      title="Campaign Recipients"
      open={open}
      setOpen={() => setOpen(false)}
      width="max-w-6xl"
    >
      <div className="flex flex-row items-center justify-between">
        <p className="text-xl font-medium">Recipient List</p>
        {/* @ts-ignore */}
        <ButtonInput
          onClick={handleExportCSV}
          disabled={exportLoading}
        >
          <div className="flex flex-row items-center space-x-2">
            {exportLoading ? <LoadingWheel width="w-5" height="h-5" /> : <DocumentIcon className="w-5 h-5" />}
            <span>{exportLoading ? "Exporting..." : "Export CSV"}</span>
          </div>
        </ButtonInput>
      </div>
      <div>
        <Table<INurtureCampaignRecipientStatistic>
          url={`/marketing/${agency.id}/nurture/${nurtureRuleId}/recipients`}
          queryKey={nurtureCampaignQueryKeys.recipients(agency.id, nurtureRuleId)}
          columns={cols}
        />
      </div>
    </Modal>
  );
} 